import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CreditCard, DollarSign, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { usePortalInvoices, usePortalPayInvoice, usePortalValidate } from '@/hooks/use-projects';
import { toast } from '@/app/components/ui/simple-toaster';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly } from '@/lib/date-utils';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-indigo-100 text-indigo-800',
  paid: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  void: 'bg-gray-100 text-gray-600',
};

export function PortalInvoicesPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { data: invoices, isLoading } = usePortalInvoices(token ?? '');
  const { data: session } = usePortalValidate(token ?? '');
  const payInvoice = usePortalPayInvoice(token ?? '');

  const paymentStatus = searchParams.get('payment');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const totalOutstanding = invoices
    ?.filter((i) => !['paid', 'void', 'cancelled'].includes(i.status))
    .reduce((sum, i) => sum + Number(i.balance_due), 0) ?? 0;

  const totalPaid = invoices
    ?.filter((i) => i.status === 'paid')
    .reduce((sum, i) => sum + Number(i.total), 0) ?? 0;
  const pageCurrency = session?.currency ?? 'USD';

  const handlePay = async (invoiceId: number) => {
    try {
      const { checkout_url } = await payInvoice.mutateAsync(invoiceId);
      // eslint-disable-next-line react-hooks/immutability
      window.location.href = checkout_url;
    } catch {
      toast.error('Payment Error', {
        description: 'Failed to start payment. Please try again.',
      });
    }
  };

  return (
    <>
      <nav className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/portal/${token}`}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Projects
            </Link>
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Payment status banner */}
        {paymentStatus === 'success' && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <p className="text-green-800 font-medium">
                Payment submitted successfully! It may take a moment for the invoice status to update.
              </p>
            </CardContent>
          </Card>
        )}
        {paymentStatus === 'cancelled' && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <p className="text-yellow-800 font-medium">
                Payment was cancelled. You can try again at any time.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Total Invoices</p>
                  <p className="text-2xl font-bold">{invoices?.length ?? 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-sm text-gray-500">Outstanding Balance</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatMoney(totalOutstanding, pageCurrency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm text-gray-500">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatMoney(totalPaid, pageCurrency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">All Invoices</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {invoices && invoices.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Balance Due</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-mono text-sm font-medium">
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell className="text-gray-600">{invoice.project_name}</TableCell>
                      <TableCell className="text-gray-600">
                        {formatDateOnly(invoice.issue_date)}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {formatDateOnly(invoice.due_date)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[invoice.status] ?? ''}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatMoney(invoice.total, invoice.currency ?? 'USD')}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatMoney(invoice.balance_due, invoice.currency ?? 'USD')}
                      </TableCell>
                      <TableCell className="text-right">
                        {invoice.can_pay_online && invoice.online_payment_available && (
                          <Button
                            size="sm"
                            onClick={() => handlePay(invoice.id)}
                            disabled={payInvoice.isPending}
                          >
                            {payInvoice.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <CreditCard className="h-4 w-4 mr-1" />
                            )}
                            Pay now
                          </Button>
                        )}
                        {invoice.can_pay_online && invoice.online_payment_available === false && (
                          <p className="max-w-64 text-xs text-gray-600">
                            Online payment isn't available in {invoice.currency ?? 'USD'}.
                            {' '}Contact {invoice.organization_name ?? 'the organization'} for payment details.
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No invoices found.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </>
  );
}
